<template>
	<div
		class="paywall-preview-modal"
		:class="{ '-permanent': isPermanent, '-visible': isVisible }"
		@click.stop="handleClick"
		@mouseenter="handleMouseEnter"
		@mouseleave="handleMouseLeave"
	>
		<div class="paywall-preview-modal__content">
			<div class="paywall-preview-modal__title">
				{{ titleText }}
			</div>
			<div class="paywall-preview-modal__subtitle">
				{{ subtitleText }}
			</div>
			<div
				class="paywall-preview-modal__buttons"
			>
				<Button
					v-if="isEmbedded"
					class="paywall-preview-modal__button"
					theme="dark"
					modifier="primary"
					:size="isMobile ? 'small' : 'medium'"
					:text="ctaText"
					:link="embeddedActionLink"
					target="_blank"
					@click.stop
				/>
				<Button
					v-else
					class="paywall-preview-modal__button"
					theme="dark"
					modifier="primary"
					:size="isMobile ? 'small' : 'medium'"
					:text="ctaText"
					@click.stop="handlePay"
				/>
				<Button
					v-show="(initialState || paused) && canContinue"
					class="paywall-preview-modal__button"
					theme="dark"
					modifier="tertiary"
					:size="isMobile ? 'small' : 'medium'"
					:text="continueButtonText"
					:disabled="!canContinue"
					icon="button-play-filled"
					@click.stop="handleContinue"
				/>
			</div>
		</div>
	</div>
</template>

<script>
import { mapGetters } from 'vuex';
import Button from '@shared/components/Button';
import { playerBus } from '@videotools/js/events';
import storage from '@shared/js/storage';
import { formatTimestampAsMinutesSeconds } from '@shared/utils/timeUtils';
import laroute from '@laroute';

export default {
	name:       'PaywallPreviewOverlay',
	components: { Button },
	props:      {
		initialPreviewDuration: {
			type:     Number,
			required: true,
		},
		previewLimitReached: {
			type:    Boolean,
			default: false,
		},
		event: {
			type:     Object,
			required: true,
		},
	},
	data() {
		return {
			toggled:              false,
			remainingPreviewTime: null,
			paused:               false,
			initialState:         true,
			playbackStarted:      false,
		};
	},
	computed: {
		...mapGetters({
			isEmbedded: 'app/isEmbedded',
			isMobile:   'device/isMobile',
		}),
		isPermanent() {
			return this.previewLimitReached || this.toggled || (this.remainingPreviewTime && this.remainingPreviewTime <= 0);
		},
		canContinue() {
			return !this.previewLimitReached && this.remainingPreviewTime > 0;
		},
		remainingPreviewTimeFormatted() {
			return formatTimestampAsMinutesSeconds(this.remainingPreviewTime);
		},
		isVisible() {
			return this.toggled || this.initialState || this.isPermanent;
		},
		isLoginWallPreview() {
			return this.event?.isLoginProtected && !this.event?.isPaymentProtected;
		},
		titleText() {
			return this.$I18n.trans(this.isLoginWallPreview ? 'payments.preview.login_title' : 'payments.preview.title');
		},
		subtitleText() {
			return this.$I18n.trans(this.isLoginWallPreview ? 'payments.preview.login_subtitle' : 'payments.preview.subtitle');
		},
		ctaText() {
			return this.$I18n.trans(this.isLoginWallPreview ? 'payments.preview.login_now' : 'payments.preview.buy_now');
		},
		loginUrl() {
			const loginRoute = laroute.route('arena.login');
			return `${loginRoute}?redirectUrl=${encodeURIComponent(this.event.url)}`;
		},
		embeddedActionLink() {
			return this.isLoginWallPreview ? this.loginUrl : `${this.event.url}#buy`;
		},
		continueButtonText() {
			if (this.initialState) {
				return $I18n.trans('payments.preview.preview');
			}

			return this.isMobile ? $I18n.trans('payments.preview.continue_short') : $I18n.trans('payments.preview.continue', { remaining: this.remainingPreviewTimeFormatted });
		},
	},
	mounted() {
		if (['checkout', 'confirm', 'login'].includes(window.location.hash.replace('#', ''))) {
			// There should be no preview mode when the user started the payment process
			this.handleLimitReached();
		}

		const remainingTime = storage.getPreviewTime(this.event.id);

		if (remainingTime === 0) {
			this.handleLimitReached();
		} else {
			this.registerPreviewInterval = setInterval(() => {
				if (playerBus.isPlaying) {
					this.playbackStarted = true;
				}

				if (this.playbackStarted) {
					storage.registerPreviewTime(this.event.id, this.initialPreviewDuration, 1);
					this.remainingPreviewTime = storage.getPreviewTime(this.event.id);
					if (this.remainingPreviewTime <= 0) {
						this.handleLimitReached();
						clearInterval(this.registerPreviewInterval);
					}
				}
			}, 1000);
		}
	},
	methods: {
		handlePay() {
			playerBus.pause();
			if (window.eventBus) {
				window.eventBus.$emit(this.isLoginWallPreview ? 'modal:LoginWallModal' : 'modal:PaywallModal');
			}
		},
		handleContinue() {
			this.toggled = false;
			this.paused = false;
			this.initialState = false;
			this.$emit('preview-started');
			playerBus.play();
		},
		handleClick() {
			if (this.initialState) {
				return;
			}

			if (!playerBus.isPlaying && this.canContinue) {
				playerBus.play();
				this.toggled = false;
				this.paused = false;
			} else {
				playerBus.pause();
				this.toggled = true;
				this.paused = true;
			}
		},
		handleMouseEnter() {
			this.toggled = true;
		},
		handleMouseLeave() {
			this.toggled = false;
		},
		handleLimitReached() {
			this.$emit('limit-reached');
		},
	},
};
</script>

<style lang="scss" scoped>
@import '@shared/sass/shared-variables';
@import '@videotools/sass/_fonts';

.paywall-preview-modal {
	position: absolute;
	top: 0;
    left: 0;
    right: 0;
    bottom: 0;
	transition: background-color 0.4s;
	opacity: 0;
	background-color: rgba(0, 0, 0, 0);
	z-index: 11;

	&.-visible, &.-permanent {
		opacity: 1;
    	background-color: rgba(0, 0, 0, 0.8);
	}
}

.paywall-preview-modal__title {
	@include text-md-3($bold: true);
	margin-bottom: $sp2;

	@include size(sm-up) {
		@include text-lg-1($bold: true);
	}

	@include size(md-up) {
		@include text-lg-3($bold: true);
	}
}

.paywall-preview-modal__subtitle {
	@include text-sm-2;

	@include size(sm-up) {
		@include text-md-1;
	}

	@include size(md-up) {
		@include text-md-3();
	}
}

.paywall-preview-modal__content {
    top: 50%;
	transform: translateY(-50%);
    left: 3%;
    position: absolute;
    max-width: 93vw;

	@include size(sm-up) {
		transform: none;
		top: unset;
		bottom: 20%;
		left: 50px;
		max-width: 70%;
	}
}

.paywall-preview-modal__buttons {
	margin-top: $sp5;
	display: flex;
	opacity: 0;
	pointer-events: none;

	.paywall-preview-modal.-visible & {
		opacity: 1;
		pointer-events: all;
	}
}

.paywall-preview-modal__button {
	margin-right: $sp4;
}
</style>
